import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, MessageSquare, Share2, Linkedin, Twitter, Facebook, UserPlus } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";

const PITCH =
  "Join me on The Emerging Market — a platform powering the work that moves Alabama communities forward.";

type Props = {
  variant?: "card" | "plain";
};

export default function InviteFriendsCard({ variant = "card" }: Props) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const inviteUrl = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://emrkt.co";
    const ref = user?.id ? user.id.slice(0, 8) : "";
    return ref ? `${origin}/auth?ref=${ref}` : `${origin}/auth`;
  }, [user?.id]);

  const shareText = `${PITCH} ${inviteUrl}`;
  const encodedUrl = encodeURIComponent(inviteUrl);
  const encodedText = encodeURIComponent(PITCH);
  const encodedShare = encodeURIComponent(shareText);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy it.");
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: "The Emerging Market",
        text: PITCH,
        url: inviteUrl,
      });
    } catch {
      // user cancelled — no toast needed
    }
  };

  const mailtoHref = `mailto:?subject=${encodeURIComponent(
    "Join me on The Emerging Market",
  )}&body=${encodedShare}`;
  const smsHref = `sms:?&body=${encodedShare}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const body = (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        <Button onClick={handleCopy} className="shrink-0">
          <Copy className="mr-2 h-4 w-4" />
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={handleNativeShare}>
          <Share2 className="mr-2 h-4 w-4" /> Share
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={mailtoHref}>
            <Mail className="mr-2 h-4 w-4" /> Email
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={smsHref}>
            <MessageSquare className="mr-2 h-4 w-4" /> Text
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={twitterHref} target="_blank" rel="noreferrer noopener">
            <Twitter className="mr-2 h-4 w-4" /> X
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={linkedinHref} target="_blank" rel="noreferrer noopener">
            <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={facebookHref} target="_blank" rel="noreferrer noopener">
            <Facebook className="mr-2 h-4 w-4" /> Facebook
          </a>
        </Button>
      </div>
    </div>
  );

  if (variant === "plain") return body;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserPlus className="h-5 w-5 text-primary" />
          Invite friends to The Emerging Market
        </CardTitle>
        <CardDescription>
          Grow the shareholder network — share your personal link with anyone who should be at the table.
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}